<template>
  <div class="container">
    <div class="dropdown-container">
      <!-- Distributor -->
      <div class="dropdown-item">
        <label>Select Distributor</label>
        <select id="distributorSelect" v-model="selectedDistributorId" @change="onDistributorChange"
          :disabled="isDistributorLocked || isBusy">
          <option value="">Select Distributor</option>

          <option v-if="isAsmUser || isFsmUser || isOtherUser" :value="ALL">
            All Distributors
          </option>

          <option v-for="d in distributorList" :key="d.profile_id ?? d.id" :value="String(d.profile_id ?? d.id)">
            {{ d.business_name || d.business_display_name || d.name || ("Distributor #" + (d.profile_id ?? d.id)) }}
          </option>
        </select>
      </div>

      <!-- Dealer -->
      <div class="dropdown-item">
        <label>Select Dealer</label>
        <select id="dealerSelect" v-model="selectedDealerId"
          :disabled="isBusy || !selectedDistributorId || selectedDistributorId === ALL">
          <!-- All Dealers default option -->
          <option :value="ALL">
            All Dealers
          </option>

          <!-- ✅ IMPORTANT: value must be dealer PROFILE id (matches invoices.dealer_id) -->
          <option v-for="d in dealerList" :key="dealerProfileIdOf(d)" :value="String(dealerProfileIdOf(d))">
            {{ dealerLabelOf(d) }}
          </option>
        </select>
      </div>

      <!-- Start Date -->
      <div class="dropdown-item">
        <label>Start Date</label>
        <input type="date" v-model="startDate" :max="currentDate" @change="handleDateChange"
          @blur="validateStartDate(true)" />
      </div>

      <!-- End Date -->
      <div class="dropdown-item">
        <label>End Date</label>
        <input type="date" v-model="endDate" :max="currentDate" @change="handleDateChange"
          @blur="validateEndDate(true)" />
      </div>

      <!-- Search -->
      <div class="dropdown-item action-item search-container">
        <button @click="performSearch(false)" class="btn button-default" :disabled="isBusy">
          <i v-if="loadingReport" class="fa fa-spinner fa-spin me-1"></i>
          {{ loadingReport ? "Searching..." : "Search" }}
        </button>
      </div>

      <!-- Export -->
      <div class="dropdown-item action-item search-container">
        <button @click="exportToExcel" class="btn button-export" :disabled="!hasData">
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isBusy" class="loading-container mt-4">
      <div class="loading-card">
        <div class="loading-spinner-wrapper">
          <i class="fa fa-spinner fa-spin loading-icon"></i>
        </div>
        <h6 class="loading-title">
          <span v-if="loadingInit">Loading initial configurations...</span>
          <span v-else-if="loadingDealers">Loading dealers...</span>
          <span v-else-if="loadingReport">Loading Dealer VAT Report...</span>
          <span v-else>Loading data, please wait...</span>
        </h6>
        <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
      </div>
    </div>

    <!-- Tables -->
    <div v-if="!isBusy && hasData" class="report-tables-wrapper">
      <!-- Section 1: Tax Invoices -->
      <div class="card mt-3">
        <div class="card-header-custom">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h6 class="mb-0 fw-bold text-primary">
              <i class="fa fa-file-text-o me-2"></i>Tax Invoices
              <span class="badge bg-primary ms-2">{{ filteredTaxInvoices.length }}</span>
              <span v-if="taxSearchQuery && filteredTaxInvoices.length !== taxInvoices.length" class="text-muted fs-small ms-1">
                (of {{ taxInvoices.length }})
              </span>
            </h6>

            <!-- Search Bar on the Right Side -->
            <div class="table-search-box">
              <i class="fa fa-search search-icon"></i>
              <input
                type="text"
                v-model="taxSearchQuery"
                placeholder="Search invoice no or business name..."
                class="table-search-input"
              />
              <button
                v-if="taxSearchQuery"
                @click="taxSearchQuery = ''"
                class="search-clear-btn"
                title="Clear search"
                type="button"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="table-scroll">
          <table class="table table-striped table-bordered tax-table">
            <thead>
              <tr>
                <th>Serial No</th>
                <th>Invoice Date</th>
                <th>Tax Invoice No</th>
                <th>Purchaser's TIN</th>
                <th>Name of the Purchaser</th>
                <th>Description</th>
                <th class="text-end">Value of Supply</th>
                <th class="text-end">VAT Amount</th>
                <th class="text-end">Total Amount</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!filteredTaxInvoices.length">
                <td colspan="9" class="text-center text-muted py-3">
                  {{ taxSearchQuery ? 'No Tax Invoices matching "' + taxSearchQuery + '"' : 'No Tax Invoices found.' }}
                </td>
              </tr>
              <tr v-for="(row, i) in filteredTaxInvoices" :key="'tax-' + i" :class="{ 'inactive-dealer-row': isInactiveDealer(row) }">
                <td>{{ row.serial_no ?? (i + 1) }}</td>
                <td>{{ row.invoice_date || "-" }}</td>
                <td>{{ row.invoices_no || row.tax_invoice_no || "-" }}</td>
                <td>{{ row.purchaser_tin || row.dealer_tin || "-" }}</td>
                <td>{{ row.purchaser_name || row.dealer_name || "-" }}</td>
                <td>LAUGFS</td>
                <td class="text-end">{{ formatNumber(row.values_of_supply ?? row.value_of_supply) }}</td>
                <td class="text-end">{{ formatNumber(row.vat ?? row.vat_amount) }}</td>
                <td class="text-end">{{ formatNumber(row.gross_total) }}</td>
              </tr>
            </tbody>

            <tfoot v-if="filteredTaxInvoices.length">
              <tr class="table-total-row">
                <th colspan="6" class="text-end fw-bold">Total:</th>
                <th class="text-end fw-bold">{{ formatNumber(taxTotalValueOfSupply) }}</th>
                <th class="text-end fw-bold">{{ formatNumber(taxTotalVat) }}</th>
                <th class="text-end fw-bold">{{ formatNumber(taxTotalGross) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Section 2: Non-Tax Invoices -->
      <div class="card mt-4">
        <div class="card-header-custom">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <h6 class="mb-0 fw-bold text-success">
              <i class="fa fa-list-alt me-2"></i>Non-Tax Invoices
              <span class="badge bg-success ms-2">{{ filteredNonTaxInvoices.length }}</span>
              <span v-if="nonTaxSearchQuery && filteredNonTaxInvoices.length !== nonTaxInvoices.length" class="text-muted fs-small ms-1">
                (of {{ nonTaxInvoices.length }})
              </span>
            </h6>

            <!-- Search Bar on the Right Side -->
            <div class="table-search-box">
              <i class="fa fa-search search-icon"></i>
              <input
                type="text"
                v-model="nonTaxSearchQuery"
                placeholder="Search invoice no or business name..."
                class="table-search-input"
              />
              <button
                v-if="nonTaxSearchQuery"
                @click="nonTaxSearchQuery = ''"
                class="search-clear-btn"
                title="Clear search"
                type="button"
              >
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="table-scroll">
          <table class="table table-striped table-bordered non-tax-table">
            <thead>
              <tr>
                <th>Serial No</th>
                <th>Invoice Date</th>
                <th>Invoice No</th>
                <th>Name of the Purchaser</th>
                <th>Business Type</th>
                <th>Description</th>
                <th class="text-end">Value of Supply</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="!filteredNonTaxInvoices.length">
                <td colspan="7" class="text-center text-muted py-3">
                  {{ nonTaxSearchQuery ? 'No Non-Tax Invoices matching "' + nonTaxSearchQuery + '"' : 'No Non-Tax Invoices found.' }}
                </td>
              </tr>
              <tr v-for="(row, i) in filteredNonTaxInvoices" :key="'nontax-' + i" :class="{ 'inactive-dealer-row': isInactiveDealer(row) }">
                <td>{{ row.serial_no ?? (i + 1) }}</td>
                <td>{{ row.invoice_date || "-" }}</td>
                <td>{{ row.invoices_no || "-" }}</td>
                <td>{{ row.business_name || row.purchaser_name || "-" }}</td>
                <td>
                  <span
                    :class="Number(row.is_government_business) === 1 ? 'badge-govt' : 'badge-non-govt'"
                  >
                    {{ Number(row.is_government_business) === 1 ? 'Government' : 'Non-Government' }}
                  </span>
                </td>
                <td>LAUGFS</td>
                <td class="text-end">{{ formatNumber(row.net_total ?? row.values_of_supply) }}</td>
              </tr>
            </tbody>

            <tfoot v-if="filteredNonTaxInvoices.length">
              <!-- Breakdown: Government Business (is_government_business = 1) -->
              <tr class="table-subtotal-row govt-subtotal">
                <th colspan="6" class="text-end fw-bold">Total - Government Business:</th>
                <th class="text-end fw-bold">{{ formatNumber(nonTaxGovtNetTotal) }}</th>
              </tr>

              <!-- Breakdown: Non-Government Business (is_government_business = 0) -->
              <tr class="table-subtotal-row non-govt-subtotal">
                <th colspan="6" class="text-end fw-bold">Total - Non-Government Business:</th>
                <th class="text-end fw-bold">{{ formatNumber(nonTaxNonGovtNetTotal) }}</th>
              </tr>

              <!-- Grand Total of Non-Tax Invoices -->
              <tr class="table-total-row">
                <th colspan="6" class="text-end fw-bold">Total (Non-Tax Invoices):</th>
                <th class="text-end fw-bold">{{ formatNumber(nonTaxTotalNet) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Modern Empty State -->
    <div v-if="!isBusy && searchedOnce && !hasData" class="no-data-container mt-4">
      <div class="no-data-card">
        <div class="no-data-icon-wrapper">
          <i class="fa fa-folder-open-o no-data-icon"></i>
        </div>
        <h5 class="no-data-title">No Data Found</h5>
        <p class="no-data-text">
          There are no invoice records matching your selected filters.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";

import {
  fetchDistributors,
  fetchDealerListManagementTrue, // ✅ use supervisor requested API
  fetchDealerVatReport,
} from "@/services/dataService";

export default {
  name: "DealerVatReportForm",

  data() {
    const currentDate = new Date().toISOString().split("T")[0];
    const ALL = "__all__";

    return {
      token: localStorage.getItem("token_set") || "",
      profileId: Number(localStorage.getItem("profile_id") || 0),
      myProfileId: Number(localStorage.getItem("profile_id") || 0),

      currentDate,
      startDate: currentDate,
      endDate: currentDate,

      loadingInit: false,
      loadingDealers: false,
      loadingReport: false,

      searchedOnce: false,

      userLabel: "",
      roleKey: "OTHER",

      distributorList: [],
      selectedDistributorId: "",
      isDistributorLocked: false,

      dealerList: [],
      selectedDealerId: ALL,

      taxInvoices: [],
      nonTaxInvoices: [],

      taxSearchQuery: "",
      nonTaxSearchQuery: "",

      ALL,
      redirecting: false,
    };
  },

  computed: {
    isDistributorUser() { return this.roleKey === "DIS"; },
    isAsmUser() { return this.roleKey === "ASM"; },
    isFsmUser() { return this.roleKey === "FSM"; },
    isOtherUser() { return this.roleKey === "OTHER"; },

    // ✅ one busy flag for UI
    isBusy() {
      return this.loadingInit || this.loadingDealers || this.loadingReport;
    },

    hasData() {
      return this.taxInvoices.length > 0 || this.nonTaxInvoices.length > 0;
    },

    // Search filtered invoice lists
    filteredTaxInvoices() {
      const q = (this.taxSearchQuery || "").trim().toLowerCase();
      if (!q) return this.taxInvoices;
      return this.taxInvoices.filter(r => {
        const invNo = String(r.invoices_no || r.tax_invoice_no || "").toLowerCase();
        const busName = String(r.purchaser_name || r.dealer_name || r.business_name || "").toLowerCase();
        return invNo.includes(q) || busName.includes(q);
      });
    },

    filteredNonTaxInvoices() {
      const q = (this.nonTaxSearchQuery || "").trim().toLowerCase();
      if (!q) return this.nonTaxInvoices;
      return this.nonTaxInvoices.filter(r => {
        const invNo = String(r.invoices_no || "").toLowerCase();
        const busName = String(r.business_name || r.purchaser_name || "").toLowerCase();
        return invNo.includes(q) || busName.includes(q);
      });
    },

    // Tax Totals
    taxTotalValueOfSupply() {
      return this.filteredTaxInvoices.reduce(
        (sum, r) => sum + (Number(r.values_of_supply ?? r.value_of_supply) || 0),
        0
      );
    },
    taxTotalVat() {
      return this.filteredTaxInvoices.reduce(
        (sum, r) => sum + (Number(r.vat ?? r.vat_amount) || 0),
        0
      );
    },
    taxTotalGross() {
      return this.filteredTaxInvoices.reduce(
        (sum, r) => sum + (Number(r.gross_total) || 0),
        0
      );
    },

    // Non-Tax Totals - Government Business (is_government_business = 1)
    nonTaxGovtInvoices() {
      return this.filteredNonTaxInvoices.filter(r => Number(r.is_government_business) === 1);
    },
    nonTaxGovtNetTotal() {
      return this.nonTaxGovtInvoices.reduce(
        (sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0),
        0
      );
    },

    // Non-Tax Totals - Non-Government Business (is_government_business = 0)
    nonTaxNonGovtInvoices() {
      return this.filteredNonTaxInvoices.filter(r => Number(r.is_government_business) !== 1);
    },
    nonTaxNonGovtNetTotal() {
      return this.nonTaxNonGovtInvoices.reduce(
        (sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0),
        0
      );
    },

    // Non-Tax Grand Totals
    nonTaxTotalNet() {
      return this.filteredNonTaxInvoices.reduce(
        (sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0),
        0
      );
    },
  },

  mounted() {
    this.init();
  },

  methods: {
    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    // ===== auth helpers =====
    bodyOf(x) { return x?.data ?? x; },
    msgOf(x) {
      const b = this.bodyOf(x?.response?.data ?? x);
      return String(b?.message ?? b?.error ?? x?.message ?? "");
    },

    isUnauthorizedAny(x) {
      const http = x?.response?.status ?? x?.status;
      const msg = this.msgOf(x).toLowerCase();
      const authMsg =
        /access token is invalid|unauthenticated|token.*expired|expired token|invalid token|session.*expired/;

      if (http === 401) return authMsg.test(msg);
      return http === 419 || http === 440 || http === 498 || authMsg.test(msg);
    },

    handleUnauthorized(x) {
      if (!this.isUnauthorizedAny(x)) return false;
      this.redirectToLogin();
      return true;
    },

    redirectToLogin() {
      if (this.redirecting) return;
      this.redirecting = true;

      this.toast("error", "Session expired. Please login again.");

      localStorage.removeItem("token_set");
      localStorage.removeItem("profile_id");
      localStorage.removeItem("user_label");
      localStorage.removeItem("userLabel");
      localStorage.removeItem("role");

      if (this.$router) this.$router.replace("/login");
      else window.location.replace("/login");
    },

    // ✅ active user filter (optional)
    isActiveUser(x) {
      const v = x?.is_active ?? x?.user_is_active ?? x?.app_user_is_active ?? x?.active;
      if (v === undefined || v === null) return true;
      return v === true || Number(v) === 1;
    },
    onlyActive(list) {
      return (Array.isArray(list) ? list : []).filter(this.isActiveUser);
    },

    initRoleFromStorage() {
      const label =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(label);
      const up = this.userLabel.toUpperCase().trim();

      if (up === "DISTRIBUTOR" || up.startsWith("DISTRIBUTOR") || up === "DIS" || up.startsWith("DIS_")) this.roleKey = "DIS";
      else if (up === "ASM" || up.startsWith("ASM_")) this.roleKey = "ASM";
      else if (up === "FSM" || up.startsWith("FSM_")) this.roleKey = "FSM";
      else this.roleKey = "OTHER";
    },

    // ===== dates =====
    isValidISODate(dateStr) {
      if (!dateStr || typeof dateStr !== "string") return false;
      if (dateStr.includes("/")) return false;
      const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return false;
      const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
      const dt = new Date(y, mo - 1, d);
      return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d;
    },

    validateStartDate(showToast = true) {
      if (!this.startDate) return true;
      if (!this.isValidISODate(this.startDate)) {
        if (showToast) this.toast("error", "Invalid Start Date");
        this.startDate = "";
        return false;
      }
      if (this.startDate > this.currentDate) {
        if (showToast) this.toast("error", "Start Date cannot be in the future");
        this.startDate = "";
        return false;
      }
      return true;
    },

    validateEndDate(showToast = true) {
      if (!this.endDate) return true;
      if (!this.isValidISODate(this.endDate)) {
        if (showToast) this.toast("error", "Invalid End Date");
        this.endDate = "";
        return false;
      }
      if (this.endDate > this.currentDate) {
        if (showToast) this.toast("error", "End Date cannot be in the future");
        this.endDate = "";
        return false;
      }
      return true;
    },

    handleDateChange() {
      this.validateStartDate(false);
      this.validateEndDate(false);
      if (this.startDate && this.endDate && this.endDate < this.startDate) {
        this.toast("error", "End date cannot be before start date.");
        this.endDate = "";
      }
    },

    validateDatesForSearch() {
      if (!this.startDate) { this.toast("error", "Please select valid Start Date"); return false; }
      if (!this.endDate) { this.toast("error", "Please select valid End Date"); return false; }
      if (!this.validateStartDate(true)) return false;
      if (!this.validateEndDate(true)) return false;
      if (this.endDate < this.startDate) { this.toast("error", "End date cannot be before start date."); return false; }
      return true;
    },

    validateFiltersOneByOne() {
      if (!this.selectedDistributorId) {
        this.toast("error", "Please select Distributor.");
        return false;
      }
      if (this.selectedDistributorId !== this.ALL && !this.selectedDealerId) {
        this.toast("error", "Please select Dealer.");
        return false;
      }
      return true;
    },

    // ===== format =====
    formatNumber(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    isInactiveDealer(row) {
      if (!row) return false;
      return String(row.is_active) === "0" || row.is_active === false;
    },

    // ✅ dealer PROFILE id mapping (must match invoices.dealer_id)
    dealerProfileIdOf(d) {
      return (
        d?.profile_id ??
        d?.dealer_profile_id ??
        d?.dealer_id ??
        d?.profileId ??
        d?.id ??
        null
      );
    },
    dealerLabelOf(d) {
      return (
        d?.business_display_name ||
        d?.business_name ||
        d?.name ||
        `Dealer #${this.dealerProfileIdOf(d) ?? ""}`
      );
    },

    // ===== init/load =====
    async init() {
      this.loadingInit = true;
      try {
        this.token = localStorage.getItem("token_set") || "";
        if (!this.token) { this.redirectToLogin(); return; }

        this.profileId = Number(localStorage.getItem("profile_id") || 0);
        this.myProfileId = this.profileId;

        this.initRoleFromStorage();

        await this.loadDistributors();
        if (this.redirecting) return;
      } catch (e) {
        if (this.handleUnauthorized(e)) return;
      } finally {
        this.loadingInit = false;
      }

      // Initial loading: automatically fetch report data
      if (this.selectedDistributorId) {
        await this.performSearch(true);
      }
    },

    async loadDistributors() {
      try {
        const res = await fetchDistributors(this.profileId, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);
        const listRaw = body?.data || body || [];
        this.distributorList = this.onlyActive(listRaw);

        if (this.isDistributorUser && this.myProfileId) {
          if (this.distributorList.length > 0) {
            this.selectedDistributorId = String(this.distributorList[0]?.profile_id);
            this.isDistributorLocked = true;
            await this.onDistributorChange();
          }
        } else {
          this.selectedDistributorId = this.ALL;
          this.selectedDealerId = this.ALL;
          this.isDistributorLocked = false;
        }
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load distributor list.");
      }
    },

    async onDistributorChange() {
      this.selectedDealerId = this.ALL;
      this.dealerList = [];
      this.taxInvoices = [];
      this.nonTaxInvoices = [];
      this.taxSearchQuery = "";
      this.nonTaxSearchQuery = "";
      this.searchedOnce = false;

      if (!this.selectedDistributorId) return;
      if (this.selectedDistributorId === this.ALL) return;

      await this.loadDealers();
    },

    // ✅ uses supervisor requested API
    async loadDealers() {
      this.loadingDealers = true;
      try {
        const distId = Number(this.selectedDistributorId);
        if (!distId) return;

        // fetchDealerListManagementTrue(profileId, distributorId, status, token)
        const res = await fetchDealerListManagementTrue(this.profileId, distId, true, this.token);
        if (this.handleUnauthorized(res)) return;

        const body = this.bodyOf(res);

        const list =
          (Array.isArray(body?.data) && body.data) ||
          (Array.isArray(body?.dealers) && body.dealers) ||
          (Array.isArray(body) && body) ||
          [];

        this.dealerList = list
          .map(d => ({ ...d, profile_id: this.dealerProfileIdOf(d) }))
          .filter(d => d.profile_id);

        this.selectedDealerId = this.ALL;

        console.log("[DealerListManagement] dist:", distId, "dealers:", this.dealerList);
      } catch (err) {
        if (this.handleUnauthorized(err)) return;
        console.error(err?.response?.data || err);
        this.toast("error", "Failed to load dealers for selected distributor.");
      } finally {
        this.loadingDealers = false;
      }
    },

    // ===== search =====
    async performSearch(isInitial = false) {
      const initial = isInitial === true;
      this.token = localStorage.getItem("token_set") || "";
      if (!this.token) return this.redirectToLogin();

      if (!this.validateFiltersOneByOne()) return;
      if (!this.validateDatesForSearch()) { this.searchedOnce = true; return; }

      this.loadingReport = true;
      this.searchedOnce = false;
      this.taxInvoices = [];
      this.nonTaxInvoices = [];
      this.taxSearchQuery = "";
      this.nonTaxSearchQuery = "";

      try {
        const payload = {
          start_date: this.startDate,
          end_date: this.endDate,
          distributor_profile_id: this.selectedDistributorId === this.ALL ? null : Number(this.selectedDistributorId),
          dealer_profile_id:
            (this.selectedDistributorId === this.ALL || this.selectedDealerId === this.ALL)
              ? null
              : Number(this.selectedDealerId),
        };

        console.log("Dealer VAT payload:", payload);

        const apiRes = await fetchDealerVatReport(this.token, payload);
        if (this.handleUnauthorized(apiRes)) return;

        const res = this.bodyOf(apiRes);

        if (!res || res.status !== true) {
          if (!initial) {
            this.toast("error", res?.message || "Error loading Dealer VAT Report.");
          }
          return;
        }

        const data = res.data;
        if (data && typeof data === "object" && !Array.isArray(data)) {
          this.taxInvoices = Array.isArray(data.tax_invoices) ? data.tax_invoices : [];
          this.nonTaxInvoices = Array.isArray(data.non_tax_invoices) ? data.non_tax_invoices : [];
        } else if (Array.isArray(data)) {
          this.taxInvoices = data.filter(r => r.is_tax_invoice == 1 || r.vat > 0 || r.tax_invoice_no);
          this.nonTaxInvoices = data.filter(r => r.is_tax_invoice == 0);
        } else {
          this.taxInvoices = [];
          this.nonTaxInvoices = [];
        }

        if (!this.hasData) {
          if (!initial) {
            this.toast("error", "No data found for selected filters.");
          }
          return;
        }

        this.toast("success", "Report Generated successfully.");
      } catch (err) {
        if (this.handleUnauthorized(err)) return;

        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Error loading report.";

        console.error("[DealerVAT] error:", err?.response?.data || err);
        if (!initial) {
          this.toast("error", msg);
        }
      } finally {
        this.loadingReport = false;
        this.searchedOnce = true;
      }
    },

    // ===== excel export =====
    exportToExcel() {
      if (!this.hasData) {
        this.toast("error", "No data to export.");
        return;
      }

      // ---- FILTERS (Top Metadata) ----
      let distributorName = "All Distributors";
      if (this.selectedDistributorId && this.selectedDistributorId !== this.ALL) {
        const selectedDist = this.distributorList.find(
          d => String(d.profile_id ?? d.id) === String(this.selectedDistributorId)
        );
        if (selectedDist) {
          distributorName =
            selectedDist.business_name ||
            selectedDist.business_display_name ||
            selectedDist.name ||
            `Distributor #${this.selectedDistributorId}`;
        }
      }

      let dealerName = "All Dealers";
      if (this.selectedDealerId && this.selectedDealerId !== this.ALL) {
        const selectedDealer = this.dealerList.find(
          d => String(this.dealerProfileIdOf(d)) === String(this.selectedDealerId)
        );
        if (selectedDealer) {
          dealerName = this.dealerLabelOf(selectedDealer);
        }
      }

      const firstName  = localStorage.getItem("first_name") || "";
      const lastName   = localStorage.getItem("last_name")  || "";
      const userFullName = (firstName + " " + lastName).trim() || "-";
      const generatedAt  = new Date().toLocaleString("en-US", { hour12: false });

      const filterRows = [
        ["Dealer VAT Report"],
        [],
        ["Distributor", distributorName],
        ["Dealer",      dealerName],
        ["Start Date",  this.startDate  || "-"],
        ["End Date",    this.endDate    || "-"],
        [],
      ];

      const footerRows = [
        [],
        ["Generated by", userFullName],
        ["Generated at", generatedAt],
      ];

      const wb = XLSX.utils.book_new();

      // ==========================================
      // 1. SHEET 1: TAX INVOICES
      // ==========================================
      const fullColsTax = 9;
      const padToTax = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullColsTax) out.push("");
        return out.slice(0, fullColsTax);
      };

      const taxHeaderRow = [
        "Serial No",
        "Invoice Date",
        "Tax Invoice No",
        "Purchaser's TIN",
        "Name of the Purchaser",
        "Description",
        "Value of Supply",
        "VAT Amount",
        "Net Total",
      ];

      const taxDataRows = this.taxInvoices.map((r, idx) => ([
        r.serial_no ?? (idx + 1),
        r.invoice_date  || "",
        r.invoices_no || r.tax_invoice_no || "",
        r.purchaser_tin || r.dealer_tin || "",
        r.purchaser_name || r.dealer_name || "",
        "LAUGFS",
        Number(r.values_of_supply ?? r.value_of_supply) || 0,
        Number(r.vat ?? r.vat_amount) || 0,
        Number(r.gross_total) || 0,
      ]));

      // Totals for Tax Invoices
      const taxTotalValueOfSupply = this.taxInvoices.reduce(
        (sum, r) => sum + (Number(r.values_of_supply ?? r.value_of_supply) || 0),
        0
      );
      const taxTotalVat = this.taxInvoices.reduce(
        (sum, r) => sum + (Number(r.vat ?? r.vat_amount) || 0),
        0
      );
      const taxTotalGross = this.taxInvoices.reduce(
        (sum, r) => sum + (Number(r.gross_total) || 0),
        0
      );

      // Totals for Non-Tax Invoices
      const nonTaxGovTotal = this.nonTaxInvoices
        .filter(r => Number(r.is_government_business) === 1)
        .reduce((sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0), 0);

      const nonTaxNonGovTotal = this.nonTaxInvoices
        .filter(r => Number(r.is_government_business) !== 1)
        .reduce((sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0), 0);

      const nonTaxTotal = this.nonTaxInvoices
        .reduce((sum, r) => sum + (Number(r.net_total ?? r.values_of_supply) || 0), 0);

      const taxTotalRow = [
        "Total",
        "",
        "",
        "",
        "",
        "",
        taxTotalValueOfSupply,
        taxTotalVat,
        taxTotalGross,
      ];

      const nonTaxGovRow = [
        "Total Gov Payments",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        nonTaxGovTotal,
      ];

      const nonTaxNonGovRow = [
        "Total Non.Gov Payment",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        nonTaxNonGovTotal,
      ];

      const nonTaxTotalRow = [
        "Total Non Tax Invoices",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
        nonTaxTotal,
      ];

      const aoaTax = [
        ...filterRows.map(padToTax),
        padToTax(taxHeaderRow),
        ...taxDataRows.map(padToTax),
        padToTax(taxTotalRow),
        padToTax(nonTaxGovRow),
        padToTax(nonTaxNonGovRow),
        padToTax(nonTaxTotalRow),
        ...footerRows.map(padToTax),
      ];

      const wsTax = XLSX.utils.aoa_to_sheet(aoaTax);
      wsTax["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
      XLSX.utils.book_append_sheet(wb, wsTax, "Tax_Invoices");

      // ==========================================
      // 2. SHEET 2: NON-TAX INVOICES
      // ==========================================
      const fullColsNonTax = 7;
      const padToNonTax = (arr) => {
        const out = Array.isArray(arr) ? [...arr] : [];
        while (out.length < fullColsNonTax) out.push("");
        return out.slice(0, fullColsNonTax);
      };

      const nonTaxHeaderRow = [
        "Serial No",
        "Invoice Date",
        "Invoice No",
        "Name of the Purchaser",
        "Business Type",
        "Description",
        "Value of Supply",
      ];

      const nonTaxDataRows = this.nonTaxInvoices.map((r, idx) => ([
        r.serial_no ?? (idx + 1),
        r.invoice_date  || "",
        r.invoices_no   || "",
        r.business_name || r.purchaser_name || "",
        Number(r.is_government_business) === 1 ? "Government" : "Non-Government",
        "LAUGFS",
        Number(r.net_total ?? r.values_of_supply) || 0,
      ]));

      const govtSubtotalRow = [
        "Total - Government Business",
        "",
        "",
        "",
        "",
        "",
        nonTaxGovTotal,
      ];

      const nonGovtSubtotalRow = [
        "Total - Non-Government Business",
        "",
        "",
        "",
        "",
        "",
        nonTaxNonGovTotal,
      ];

      const nonTaxGrandTotalRow = [
        "Grand Total (Non-Tax Invoices)",
        "",
        "",
        "",
        "",
        "",
        nonTaxTotal,
      ];

      const aoaNonTax = [
        ...filterRows.map(padToNonTax),
        padToNonTax(nonTaxHeaderRow),
        ...nonTaxDataRows.map(padToNonTax),
        padToNonTax(govtSubtotalRow),
        padToNonTax(nonGovtSubtotalRow),
        padToNonTax(nonTaxGrandTotalRow),
        ...footerRows.map(padToNonTax),
      ];

      const wsNonTax = XLSX.utils.aoa_to_sheet(aoaNonTax);
      wsNonTax["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
      XLSX.utils.book_append_sheet(wb, wsNonTax, "Non_Tax_Invoices");

      // Save workbook
      XLSX.writeFile(wb, `Dealer_VAT_Report_${this.startDate}_to_${this.endDate}.xlsx`);
      this.toast("success", "Excel exported successfully.");
    },
  },
};
</script>

<style scoped>
.table-scroll {
  max-height: 500px;
  overflow: auto;
  border: 1px solid #ddd;
  margin-top: 10px;
  position: relative;
}

.table thead th {
  top: 0;
  background: #f9f9f9;
  z-index: 10;
  white-space: nowrap;
}

.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  width: calc(100% - 20px);
  box-sizing: border-box;
}

.dropdown-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
  align-items: end;
}

.dropdown-item {
  width: 100%;
}

.dropdown-item.action-item {
  display: flex;
  align-items: flex-end;
}

.dropdown-item.action-item .btn {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

select,
input[type="date"] {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #fff;
}

select:focus,
input[type="date"]:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

select:disabled,
input[type="date"]:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

.btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn.button-default {
  background-color: #007bff;
  color: #fff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-export {
  background-color: #28a745;
  color: #fff;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

/* Medium devices (tablets): 2 columns / separate lines */
@media (min-width: 576px) and (max-width: 1023px) {
  .dropdown-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

/* Max length devices (desktop / wide screens): single line */
@media (min-width: 1024px) {
  .dropdown-container {
    grid-template-columns: minmax(180px, 2fr) minmax(180px, 2fr) minmax(130px, 1fr) minmax(130px, 1fr) auto auto;
    gap: 10px;
    align-items: end;
  }

  .dropdown-item {
    width: auto;
  }

  .dropdown-item.action-item .btn {
    width: auto;
  }
}

/* Modern Full-Sized Loading Indicator */
.loading-container {
  width: 100%;
  margin-top: 24px;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* Modern Empty State Styling - Full sized width */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}

.text-end {
  text-align: right;
}

.card-header-custom {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

/* Table Search Box in Card Header */
.table-search-box {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 320px;
}

.table-search-box .search-icon {
  position: absolute;
  left: 10px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}

.table-search-input {
  width: 100%;
  height: 34px;
  padding: 6px 30px 6px 30px;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background-color: #ffffff;
  color: #1e293b;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.table-search-input:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.table-search-input::placeholder {
  color: #94a3b8;
  font-size: 12px;
}

.search-clear-btn {
  position: absolute;
  right: 8px;
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  border-radius: 50%;
  transition: color 0.15s ease;
}

.search-clear-btn:hover {
  color: #475569;
}

.fs-small {
  font-size: 12px;
  font-weight: 500;
}

/* Base styles for sticky/fixed footer rows in scrollable table */
.table-scroll table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
}

.table-scroll table tfoot tr {
  height: 38px;
  border: none !important;
}

.table-scroll table tfoot th,
.table-scroll table tfoot td {
  z-index: 8;
  white-space: nowrap;
  vertical-align: middle;
  padding: 8px 12px;
  line-height: 20px;
  height: 38px;
  box-sizing: border-box;
  border: none !important;
  box-shadow: none !important;
}

/* 1. Tax Table Footer - single fixed total row at bottom */
.table-scroll .tax-table tfoot .table-total-row th {
  bottom: 0;
  background-color: #e9ecef !important;
  color: #212529;
  font-weight: 700 !important;
  border: none !important;
  box-shadow: none !important;
}

/* 2. Non-Tax Table Footer - stacked fixed subtotals & grand total */
/* Row 1: Government Business */
.table-scroll .non-tax-table tfoot .govt-subtotal th {
  bottom: 76px;
  background-color: #eef6ff !important;
  color: #0d6efd;
  font-weight: 600 !important;
  border: none !important;
  box-shadow: none !important;
}

/* Row 2: Non-Government Business */
.table-scroll .non-tax-table tfoot .non-govt-subtotal th {
  bottom: 38px;
  background-color: #f0fdf4 !important;
  color: #198754;
  font-weight: 600 !important;
  border: none !important;
  box-shadow: none !important;
}

/* Row 3: Grand Total Non-Tax */
.table-scroll .non-tax-table tfoot .table-total-row th {
  bottom: 0;
  background-color: #e9ecef !important;
  color: #212529;
  font-weight: 700 !important;
  border: none !important;
  box-shadow: none !important;
}

/* Inactive Dealer Row - lite red background */
.inactive-dealer-row,
.inactive-dealer-row td {
  background-color: #ffebee !important;
}

.table-hover tbody tr.inactive-dealer-row:hover td {
  background-color: #ffcdd2 !important;
}

.badge-govt {
  display: inline-block;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  background-color: #cff4fc;
  color: #055160;
  border: 1px solid #b6effb;
}

.badge-non-govt {
  display: inline-block;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  background-color: #e2e3e5;
  color: #41464b;
  border: 1px solid #d3d6d8;
}

.fw-bold {
  font-weight: 700 !important;
}
</style>